import React, { useEffect } from 'react';

/**
 * AdDrawer — bottom sheet with the full details for a sponsored business.
 * Used fixed to the viewport from the feed ad card, and absolute within the
 * story frame from ad story slides. Pink is reserved for the CTA button only.
 */
export default function AdDrawer({ ad, analytics, onClose, position = 'fixed' }) {
    const businessUrl = `/businesses/${ad.businessId}/`;

    useEffect(() => {
        if (position !== 'fixed' || !document.body) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [position]);

    return (
        <div className={`${position === 'fixed' ? 'fixed inset-0 z-50' : 'absolute inset-0 z-30'}`} role="dialog" aria-modal="true" aria-label={`${ad.title} details`}>
            <div className="absolute inset-0 bg-black/60 animate-fade-in" onClick={onClose} aria-hidden="true" />

            <div
                className={[
                    'absolute bottom-0 bg-white rounded-t-3xl shadow-2xl flex flex-col animate-drawer-up',
                    position === 'fixed'
                        ? 'inset-x-0 h-[90%] md:mx-auto md:top-6 md:bottom-6 md:h-auto md:w-full md:max-w-lg md:rounded-3xl'
                        : 'inset-x-0 h-[90%]',
                ].join(' ')}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-center pt-3 pb-1">
                    <span className="w-10 h-1.5 rounded-full bg-gray-300" />
                </div>

                <div className="flex items-center gap-3 px-5 pt-2 pb-3 border-b border-gray-100">
                    {ad.avatarUrl ? (
                        <img src={ad.avatarUrl} alt="" className="w-9 h-9 rounded-full object-cover border border-gray-200" />
                    ) : null}
                    <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 leading-tight truncate">{ad.title}</p>
                        {ad.handle && <p className="text-sm text-gray-500 truncate">{ad.handle}</p>}
                    </div>
                    <span className="shrink-0 inline-block px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide bg-gray-100 text-gray-600 rounded-full">
                        Promoted
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                        aria-label="Close details"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
                    {ad.imageUrl && (
                        <div className="-mx-5 overflow-hidden -mt-1 mb-4">
                            <img src={ad.imageUrl} alt="" className="w-full h-auto object-contain" loading="lazy" />
                        </div>
                    )}

                    <h2 className="text-2xl font-bold text-gray-900 leading-snug mb-3">{ad.title}</h2>

                    {(ad.discount || ad.offerCode) && (
                        <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 mb-4">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-0.5">Member offer</p>
                            <p className="font-semibold text-gray-900">{ad.discount}</p>
                            {ad.offerCode && <p className="text-sm font-medium text-gray-700">{ad.offerCode}</p>}
                            {ad.offerNote && <p className="text-sm text-gray-600 mt-0.5">{ad.offerNote}</p>}
                        </div>
                    )}

                    {ad.description ? (
                        <div>
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">About this business</h3>
                            <p className="text-[15px] text-gray-700 leading-relaxed whitespace-pre-line">{ad.description}</p>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">Visit the business page to learn more.</p>
                    )}
                </div>

                <div className="px-5 pt-3 pb-5 border-t border-gray-100">
                    <a
                        href={businessUrl}
                        onClick={() => analytics?.trackInteraction('click_ad_drawer_cta', { businessId: ad.businessId })}
                        className="post-cta w-full"
                    >
                        Visit {ad.title}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                        </svg>
                    </a>
                </div>
            </div>
        </div>
    );
}